import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Constants } from 'src/app/app.constants';
import { Message } from 'src/app/models/message';
import { AuthService } from 'src/app/services/auth.service';
import { MerchantService } from 'src/app/services/merchant.service';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { AutomationsDialogComponent } from '../automations_dialog/automations_dialog.component';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';
import { UserNotification } from 'src/app/models/userNotification';
import { Conversation } from 'src/app/models/conversation';
import { ChatService } from 'src/app/services/chat.service';
import { MessagesDialogComponent } from '../messages_dialog/messages_dialog.component';
import { ChatMessage } from 'src/app/models/chatMessage';
import { ConversationSettingsDialogComponent } from '../conversation-settings/conversation-settings-dialog.component';
import { PermissionsService } from 'src/app/services/permissions.service';
import { map, Observable } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AutomationsListComponent } from '../automations-list/automations-list.component';
import { AutomationDialogComponent } from '../automation-dialog/automation-dialog.component';
import { EngageUpgradeDialogComponent } from '../engage-upgrade-dialog/engage-upgrade-dialog.component';

interface EngageSectionCard {
  key: string;
  title: string;
  eyebrow: string;
  icon: string;
  description: string;
  stat: string | null;
  action: string;
  enabled: boolean;
}

@Component({
  selector: 'engage',
  templateUrl: './engage.component.html',
  styleUrls: ['./engage.component.css'],
})
export class EngageComponent {
  @Input() smallTitle = false;
  @Input() title = '';
  @Input() subtitle: string = '';
  @ViewChild('messageTable') messageTable: any;
  @ViewChild('birthdayTable') birthdayTable: any;
  @ViewChild('conversationTable') conversationTable: any;
  @ViewChild('automationsList') automationsList?: AutomationsListComponent;

  numberOfActiveAutomations = 0;
  promotionCounts = {
    upcoming: 0,
    live: 0,
    historic: 0,
  };
  hasPromotionsAccess = false;
  birthdayClubEnabled = false;
  hasBirthdayClubAccess = false;
  menuOptions: any[] = [];
  tableMenuOptions = [{ title: 'Edit Nickname', icon: 'edit' }];
  allChecked: any = { 0: { checked: false } };
  messagePageIndex = 0;
  conversationPageIndex = 0;
  birthdayPageIndex = 0;
  pageSize = 10;
  tableSkeletonRows = Array.from({ length: 10 }, () => ({ __loading: true }));
  private readonly minTableLoadingMs = 650;
  private tableLoadingStartedAt: { [key: string]: number } = {};
  messageLength = 0;
  messageCounts = {
    scheduled: 0,
    drafts: 0,
    sent: 0,
  };
  conversationLength = 0;
  conversationCounts = {
    unreadMessages: 0,
    conversations: 0,
  };
  birthdayLength = 0;
  messages: Message[] = [];
  conversations: Conversation[] = [];
  birthdayNotifications: UserNotification[] = [];
  messagesLoading = true;
  conversationsLoading = false;
  birthdayLoading = false;
  automationsLoading = false;
  sendingPush = false;
  error = false;
  scrollTimeout: any;
  suppressPaging: boolean = false;
  messagesRange = '';
  conversationsRange = '';
  birthdayRange = '';
  saving = false;

  tabs = ['messages'];
  selectedTab = 'messages';
  selectedIndex = 0;
  isLandingPage = true;
  homeCardsLoading = true;
  private homeCardLoadsPending = 0;

  showConversationsTab = false;
  showAutomationsTab = true;
  hasAutomationsAccess = false;
  canViewCustomers = false;
  canEngage = false;
  sectionCards: EngageSectionCard[] = [];

  constructor(
    public dialog: MatDialog,
    public constants: Constants,
    private router: Router,
    public authService: AuthService,
    private merchantService: MerchantService,
    private mixpanel: AnalyticsAbstract,
    private route: ActivatedRoute,
    private chatService: ChatService,
    private ps: PermissionsService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.showConversationsTab =
      this.authService.getNestedUserProperty('merchant', 'conversations')
        ?.active ?? false;

    this.canViewCustomers = this.ps.permissions.customers.view();
    this.canEngage = this.ps.permissions.engage.view();
    this.refreshSectionCards();

    this.menuOptions = [
      {
        title: 'Send Push',
        icon: 'mark_chat_unread',
      },
      {
        title: 'New Automation',
        icon: 'bolt',
      },
      {
        title: 'Conversation Settings',
        icon: 'settings',
      },
    ];
  }

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(['(max-width: 950px)'])
    .pipe(map((result) => result.matches));

  ngOnInit() {
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');
    this.route.params.subscribe((params) => {
      const section = params['section'];
      if (section) {
        this.isLandingPage = false;
        this.selectSection(section);
        this.loadData();
      } else {
        this.isLandingPage = true;
        this.stopSectionLoading();
      }
    });

    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (tab) {
        this.isLandingPage = false;
        this.selectSection(tab);
        this.loadData();
      }
    });

    this.getAutomations();
  }

  private refreshSectionCards(): void {
    this.sectionCards = [
      {
        key: 'messages',
        title: 'Push Notifications',
        eyebrow: 'Broadcasts',
        icon: 'mark_chat_unread',
        description:
          'Send one-off updates to selected customers and track delivery, clicks and saved drafts.',
        stat: this.canEngage ? this.messageCardStat : 'Upgrade',
        action: this.canEngage ? 'Push Notifications' : 'Upgrade',
        enabled: this.canEngage,
      },
      {
        key: 'automations',
        title: 'Automations',
        eyebrow: 'Always-on',
        icon: 'bolt',
        description:
          'Build always-on flows that send messages or rewards on a schedule or customer trigger.',
        stat: this.numberOfActiveAutomations
          ? `${this.numberOfActiveAutomations} active`
          : this.hasAutomationsAccess
            ? null
            : 'Upgrade',
        action: this.hasAutomationsAccess ? 'Automations' : 'Upgrade',
        enabled: this.hasAutomationsAccess,
      },
      {
        key: 'promotions',
        title: 'Promotions',
        eyebrow: 'Campaigns',
        icon: 'campaign',
        description:
          'Run limited-time stamp or points boosts to bring customers back during key windows.',
        stat: this.hasPromotionsAccess ? this.promotionCardStat : 'Upgrade',
        action: this.hasPromotionsAccess ? 'Promotions' : 'Upgrade',
        enabled: this.canEngage && this.hasPromotionsAccess,
      },
      {
        key: 'birthday',
        title: 'Birthday Club',
        eyebrow: 'Lifecycle',
        icon: 'cake',
        description:
          'Automatically celebrate customer birthdays with a message and optional reward.',
        stat: this.hasBirthdayClubAccess
          ? this.birthdayClubEnabled
            ? 'Live'
            : 'Not active'
          : 'Upgrade',
        action: this.hasBirthdayClubAccess ? 'Birthday club' : 'Upgrade',
        enabled: this.canEngage && this.hasBirthdayClubAccess,
      },
      {
        key: 'conversations',
        title: 'Conversations',
        eyebrow: 'Inbox',
        icon: 'forum',
        description:
          'Reply to customer messages, monitor unread conversations and control inbox availability.',
        stat:
          this.canEngage && this.showConversationsTab
            ? this.conversationCardStat
            : 'Upgrade',
        action:
          this.canEngage && this.showConversationsTab
            ? 'Conversations'
            : 'Upgrade',
        enabled: this.canEngage && this.showConversationsTab,
      },
    ];
  }

  trackSectionCard(index: number, card: EngageSectionCard): string {
    return card.key;
  }

  get selectedSection() {
    return (
      this.sectionCards.find((card) => card.key === this.selectedTab) ??
      this.sectionCards[0]
    );
  }

  get pageTitle(): string {
    return this.isLandingPage
      ? this.constants.strings.engage
      : (this.selectedSection?.title ?? this.constants.strings.engage);
  }

  get selectedSectionAction(): { title: string; icon: string } {
    switch (this.selectedTab) {
      case 'automations':
        return { title: 'New Automation', icon: 'add' };
      case 'birthday':
        return { title: 'Configure', icon: 'settings' };
      case 'conversations':
        return { title: 'Configure', icon: 'settings' };
      default:
        return { title: 'Send Push Notification', icon: 'mark_chat_unread' };
    }
  }

  runSelectedSectionAction(): void {
    switch (this.selectedTab) {
      case 'automations':
        this.createAutomation();
        return;
      case 'birthday':
        this.openAutomations(1, true);
        return;
      case 'conversations':
        this.openSettings();
        return;
      default:
        this.router.navigate([Constants.routes.engagePush]);
    }
  }

  getAutomations(): void {
    this.homeCardsLoading = true;
    this.homeCardLoadsPending = 1;

    this.merchantService
      .getAutomations(true)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.birthdayClubEnabled = res?.automations?.birthdayClub?.enabled;
            this.hasPromotionsAccess =
              res?.access?.promotions && this.canEngage;
            this.setPromotionCounts(res?.promotions ?? []);

            this.hasBirthdayClubAccess =
              res?.access?.birthday && this.canEngage;
            this.hasAutomationsAccess =
              res?.access?.automations && this.canEngage;
            this.refreshSectionCards();

            this.tabs = ['messages'];
            if (this.hasBirthdayClubAccess) {
              this.tabs.push('birthday');
            }
            if (this.hasAutomationsAccess) {
              this.tabs.push('automations');
            }
            if (this.showConversationsTab) {
              this.tabs.push('conversations');
            }

            if (this.canEngage) {
              this.homeCardLoadsPending++;
              this.getMessageCounts();
              if (this.hasAutomationsAccess) {
                this.homeCardLoadsPending++;
                this.getCustomerAutomationCounts();
              }

              if (this.showConversationsTab) {
                this.homeCardLoadsPending++;
                this.getConversationCounts();
              }
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => {
        this.automationsLoading = false;
        this.completeHomeCardLoad();
      });
  }

  get tooltip(): string {
    if (this.loading) {
      return '';
    }
    if (!this.authService.merchantObj?.active) {
      return this.constants.strings.inactive;
    } else {
      return '';
    }
  }

  get emptyString(): string {
    return `No ${this.constants.strings.messages} to display`;
  }

  get messageCardStat(): string | null {
    if (this.messageCounts.scheduled) {
      return `${this.messageCounts.scheduled} scheduled`;
    }

    if (this.messageCounts.drafts) {
      return `${this.messageCounts.drafts} ${
        this.messageCounts.drafts === 1 ? 'draft' : 'drafts'
      }`;
    }

    if (this.messageCounts.sent) {
      return `${this.messageCounts.sent} sent`;
    }

    return null;
  }

  get conversationCardStat(): string | null {
    if (!this.showConversationsTab) {
      return 'Deactivated';
    }

    if (this.conversationCounts.unreadMessages) {
      return `${this.conversationCounts.unreadMessages} unread`;
    }

    if (this.conversationCounts.conversations) {
      return `${this.conversationCounts.conversations} ${
        this.conversationCounts.conversations === 1
          ? 'conversation'
          : 'conversations'
      }`;
    }

    return null;
  }

  get promotionCardStat(): string | null {
    if (this.promotionCounts.upcoming) {
      return `${this.promotionCounts.upcoming} upcoming`;
    }

    if (this.promotionCounts.live) {
      return `${this.promotionCounts.live} live`;
    }

    if (this.promotionCounts.historic) {
      return `${this.promotionCounts.historic} historic`;
    }

    return null;
  }

  getBirthdayNotifications(): void {
    this.messagesLoading = false;
    this.conversationsLoading = false;
    this.startTableLoading('birthday');
    this.merchantService
      .getBirthdayNotifications(this.pageSize, this.birthdayPageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.birthdayLength = res.totalDocs;
            this.birthdayNotifications = res.docs;

            this.birthdayRange = this.constants.pageCounter(
              this.birthdayPageIndex,
              this.pageSize,
              this.birthdayLength,
              this.birthdayNotifications?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => this.finishTableLoading('birthday'));
  }

  getConversations(): void {
    if (this.conversationsLoading) {
      return;
    }

    this.messagesLoading = false;
    this.birthdayLoading = false;
    this.startTableLoading('conversations');
    this.chatService
      .getConversations(this.pageSize, this.messagePageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.conversationLength = res.totalDocs;
            this.conversations = res.docs;
            this.setConversationCounts(res.conversationCounts);

            this.conversationsRange = this.constants.pageCounter(
              this.conversationPageIndex,
              this.pageSize,
              this.conversationLength,
              this.conversations?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => this.finishTableLoading('conversations'));
  }

  getConversationCounts(): void {
    if (!this.showConversationsTab) {
      return;
    }

    this.chatService
      .getConversations(1, 0)
      .subscribe({
        next: (res: any) => {
          this.setConversationCounts(res?.conversationCounts);
        },
      })
      .add(() => this.completeHomeCardLoad());
  }

  getSentMessages(): void {
    this.birthdayLoading = false;
    this.conversationsLoading = false;
    this.startTableLoading('messages');
    this.merchantService
      .getSentMessages(this.pageSize, this.messagePageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.messageLength = res.totalDocs;
            this.messages = res.docs;
            this.setMessageCounts(res.messageCounts);

            this.messagesRange = this.constants.pageCounter(
              this.messagePageIndex,
              this.pageSize,
              this.messageLength,
              this.messages?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => this.finishTableLoading('messages'));
  }

  private startTableLoading(
    key: 'messages' | 'birthday' | 'conversations',
  ): void {
    this.tableLoadingStartedAt[key] = Date.now();

    if (key === 'messages') {
      this.messagesLoading = true;
    } else if (key === 'birthday') {
      this.birthdayLoading = true;
    } else {
      this.conversationsLoading = true;
    }
  }

  private finishTableLoading(
    key: 'messages' | 'birthday' | 'conversations',
  ): void {
    const elapsed =
      Date.now() - (this.tableLoadingStartedAt[key] ?? Date.now());
    const remaining = Math.max(this.minTableLoadingMs - elapsed, 0);

    window.setTimeout(() => {
      if (key === 'messages') {
        this.messagesLoading = false;
      } else if (key === 'birthday') {
        this.birthdayLoading = false;
      } else {
        this.conversationsLoading = false;
      }
    }, remaining);
  }

  getMessageCounts(): void {
    this.merchantService
      .getSentMessages(1, 0)
      .subscribe({
        next: (res: any) => {
          this.setMessageCounts(res?.messageCounts);
        },
      })
      .add(() => this.completeHomeCardLoad());
  }

  getCustomerAutomationCounts(): void {
    this.merchantService
      .getCustomerAutomations()
      .subscribe({
        next: (res: any[]) => {
          this.numberOfActiveAutomations =
            res?.filter((automation) => automation?.enabled).length ?? 0;
        },
        error: () => {
          this.numberOfActiveAutomations = 0;
        },
      })
      .add(() => this.completeHomeCardLoad());
  }

  private completeHomeCardLoad(): void {
    this.homeCardLoadsPending = Math.max(this.homeCardLoadsPending - 1, 0);
    this.homeCardsLoading = this.homeCardLoadsPending > 0;
    this.refreshSectionCards();
  }

  private setMessageCounts(counts: any): void {
    if (!counts) {
      return;
    }

    this.messageCounts = {
      scheduled: counts.scheduled ?? 0,
      drafts: counts.drafts ?? 0,
      sent: counts.sent ?? 0,
    };
    this.refreshSectionCards();
  }

  private setConversationCounts(counts: any): void {
    if (!counts) {
      return;
    }

    this.conversationCounts = {
      unreadMessages: counts.unreadMessages ?? 0,
      conversations: counts.conversations ?? 0,
    };
    this.refreshSectionCards();
  }

  private setPromotionCounts(promotions: any[]): void {
    const now = new Date();

    this.promotionCounts = promotions.reduce(
      (counts, promotion) => {
        const start = promotion?.start ? new Date(promotion.start) : null;
        const end = promotion?.end ? new Date(promotion.end) : null;

        if (start && start > now) {
          counts.upcoming++;
        } else if ((!start || start <= now) && end && end > now) {
          counts.live++;
        } else {
          counts.historic++;
        }

        return counts;
      },
      {
        upcoming: 0,
        live: 0,
        historic: 0,
      },
    );
    this.refreshSectionCards();
  }

  messagePageEvent(event: any) {
    if (!this.suppressPaging) {
      this.messagePageIndex = event.offset;
      this.getSentMessages();
    }
  }

  birthdayPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.birthdayPageIndex = event.offset;
      this.getBirthdayNotifications();
    }
  }

  newMessage(): void {
    this.router.navigate([Constants.routes.location]);
  }

  get loading(): boolean {
    return (
      this.messagesLoading ||
      this.sendingPush ||
      this.saving ||
      this.birthdayLoading ||
      this.automationsLoading ||
      this.conversationsLoading
    );
  }

  get blockingLoading(): boolean {
    return this.sendingPush || this.saving || this.automationsLoading;
  }

  onClick(event: any, item: any) {
    if (
      event === this.constants.strings.pushNotification ||
      event === 'Send Push'
    ) {
      this.router.navigate([Constants.routes.engagePush]);
    }
    if (event === 'New Automation') {
      this.createAutomation();
    }
    if (event === 'Conversation Settings') {
      this.openSettings();
    }
    if (event === this.constants.strings.email) {
    }

    if (event === this.constants.strings.mute) {
      this.muteConversation(true, item);
    }

    if (event === this.constants.strings.unmute) {
      this.muteConversation(false, item);
    }

    if (event === this.constants.strings.block) {
      this.blockConversation(true, item);
    }

    if (event === this.constants.strings.unblock) {
      this.blockConversation(false, item);
    }

    if (
      event === this.constants.strings.duplicate ||
      event === this.constants.strings.edit
    ) {
      this.navigate(item);
    }

    if (event === this.constants.strings.delete) {
      const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
        data: {
          body: 'Would you like to delete this message?',
          buttonOne: 'Cancel',
          buttonTwo: 'Yes',
        },
        scrollStrategy: new NoopScrollStrategy(),
        autoFocus: false,
        width: '350px',
        disableClose: true,
        panelClass: 'custom-dialog',
      });

      dialogRef.afterClosed().subscribe((option: number) => {
        if (option == 1) {
          this.deleteMessage(item);
        }
      });
    }
  }

  openSection(section: string): void {
    if (this.homeCardsLoading) {
      return;
    }

    const card = this.sectionCards.find((item) => item.key === section);
    if (card && !card.enabled) {
      this.openUpgradeDialog(section);
      return;
    }

    if (section === 'promotions') {
      this.openAutomations(0, false, true);
      return;
    }

    if (section === 'conversations' && !this.showConversationsTab) {
      this.openSettings();
      return;
    }

    if (!section) {
      this.router.navigate([Constants.routes.engage]);
      return;
    }

    this.router.navigate([Constants.routes.engage, section]);
  }

  private openUpgradeDialog(section: string): void {
    const featureText: {
      [key: string]: { title: string; body: string; benefits: string[] };
    } = {
      messages: {
        title: 'Unlock Push Notifications',
        body: 'Upgrade to keep customers coming back with targeted announcements, saved drafts and delivery tracking.',
        benefits: [
          'Send announcements to selected customers',
          'Schedule messages around key trading moments',
          'Track sent messages, drafts and delivery',
        ],
      },
      automations: {
        title: 'Unlock Automations',
        body: 'Automations are a Growth feature. Upgrade to send messages and rewards automatically on schedules or customer triggers.',
        benefits: [
          'Welcome customers when they take a loyalty card',
          'Send scheduled reminders and rewards',
          'Create always-on flows without manual work',
        ],
      },
      promotions: {
        title: 'Unlock Promotions',
        body: 'Promotions are a Growth feature. Upgrade to run limited-time stamp and points boosts that bring customers back.',
        benefits: [
          'Run limited-time stamp and points boosts',
          'Schedule campaigns for quiet periods',
          'Encourage more repeat visits',
        ],
      },
      birthday: {
        title: 'Unlock Birthday Club',
        body: 'Birthday Club is a Growth feature. Upgrade to automatically celebrate customers with birthday messages and optional rewards.',
        benefits: [
          'Send birthday messages automatically',
          'Add optional rewards, stamps or points',
          'Create personal moments customers remember',
        ],
      },
      conversations: {
        title: 'Unlock Conversations',
        body: 'Upgrade to manage customer conversations from the portal and keep every customer reply in one place.',
        benefits: [
          'Reply to customers from the portal',
          'Keep message history in one place',
          'Control inbox availability and notifications',
        ],
      },
    };

    this.dialog.open(EngageUpgradeDialogComponent, {
      data: featureText[section],
      width: '560px',
      autoFocus: false,
      disableClose: false,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  private selectSection(section: string): void {
    const validSections = [
      'messages',
      'birthday',
      'automations',
      'conversations',
    ];

    this.selectedTab = validSections.includes(section) ? section : 'messages';
    this.selectedIndex = this.tabs.findIndex((el) => el === section);

    if (this.selectedIndex < 0) {
      this.selectedIndex = 0;
    }
  }

  private stopSectionLoading(): void {
    this.messagesLoading = false;
    this.birthdayLoading = false;
    this.conversationsLoading = false;
  }

  muteConversation(mute: boolean, conversation: Conversation) {
    this.mixpanel.track(
      mute
        ? Constants.analytics_keys.muteConversation
        : Constants.analytics_keys.unmuteConversation,
      {
        Conversation: conversation?.nanoid,
      },
    );

    this.saving = true;
    this.chatService
      .muteConversation(mute, conversation._id!)
      .subscribe({
        next: (res: any) => {
          if (res) {
            if (mute) {
              (conversation.muted ?? []).push(conversation.merchant as string);
            } else {
              const index: number = (conversation.muted ?? []).indexOf(
                conversation.merchant as string,
              );
              if (index !== -1) {
                (conversation.muted ?? []).splice(index, 1);
              }
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  blockConversation(block: boolean, conversation: Conversation) {
    this.mixpanel.track(
      block
        ? Constants.analytics_keys.blockConversation
        : Constants.analytics_keys.unblockConversation,
      {
        Conversation: conversation?.nanoid,
      },
    );

    this.saving = true;
    this.chatService
      .blockConversation(block, conversation._id!)
      .subscribe({
        next: (res: any) => {
          if (res) {
            conversation.blockedByMerchant = block;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  handleScroll() {
    this.suppressPaging = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.suppressPaging = false;
    }, 100);
  }

  openSettings(): void {
    this.dialog.open(ConversationSettingsDialogComponent, {
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '600px',
      maxHeight: '80vh',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
  }

  rowTappedNGX({ row, type }: any, birthday: boolean) {
    if (type === 'click') {
      if (row?.draft) {
        this.navigate(row);
      } else if (this.selectedTab == 'conversations') {
        row.lastMessage.seen = true;
        const dialogRef = this.dialog.open(MessagesDialogComponent, {
          data: {
            customer: row.user.name,
            conversation: row,
          },
          scrollStrategy: new NoopScrollStrategy(),
          autoFocus: false,
          width: '600px',
          maxHeight: '80vh',
          disableClose: true,
          panelClass: 'custom-dialog',
        });

        dialogRef.afterClosed().subscribe((el) => {
          this.chatService.socketDispose();
        });
      } else {
        const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
          data: {
            title: 'Message',
            body: row?.message,
            buttonOne: birthday ? undefined : 'Duplicate',
            buttonTwo: 'Close',
          },
          scrollStrategy: new NoopScrollStrategy(),
          autoFocus: false,
          width: '350px',
          disableClose: true,
          panelClass: 'custom-dialog',
        });

        dialogRef.afterClosed().subscribe((option: number) => {
          if (option == 0) {
            this.navigate(row);
          }
        });
      }
    }
  }

  navigate(row: Message): void {
    this.router.navigate([Constants.routes.engagePush, row.nanoid]);
  }

  clickRate(row: any): string {
    return row?.delivered == 0 || !row?.delivered
      ? '-'
      : (
          ((row?.delivered ?? 0) /
            (row?.trueRecipients ?? row?.recipients ?? 0)) *
          100
        ).toFixed(2) + '%';
  }

  openAutomations(
    index: number,
    hidePromotionsTab = false,
    hideBirthdayClubTab = false,
  ): void {
    this.mixpanel.track(Constants.analytics_keys.openAutomations);

    this.dialog.open(AutomationsDialogComponent, {
      data: {
        selectedIndex: index,
        hidePromotionsTab,
        hideBirthdayClubTab,
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '800px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: '80vh',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
  }

  deleteMessage(message: Message): void {
    this.mixpanel.track(Constants.analytics_keys.deleteMessage, {
      Message: message?.message,
    });

    this.saving = true;
    this.merchantService
      .deleteMessage(message._id!)
      .subscribe({
        next: (res: any) => {
          if (res) {
            window.location.reload();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  tabChanged(index: any): void {
    let tab = this.tabs[index];

    this.router.navigate([Constants.routes.engage, tab]);

    this.selectedTab = tab;
    this.isLandingPage = false;

    this.loadData();
  }

  loadData(): void {
    this.stopSectionLoading();

    if (this.selectedTab === 'messages') {
      this.getSentMessages();
    } else if (this.selectedTab === 'birthday') {
      this.getBirthdayNotifications();
    } else if (this.selectedTab === 'conversations') {
      this.getConversations();
    } else if (this.selectedTab === 'automations') {
      this.automationsLoading = false;
    }
  }

  conversationStatus(conversation: Conversation) {
    if (conversation.blockedByMerchant) {
      return 'Blocked';
    } else if (conversation.muted?.includes(conversation.merchant as string)) {
      return 'Muted';
    } else {
      return 'Active';
    }
  }

  status(message: any): string {
    if (message?.schedule?.active && !message?.schedule?.sent) {
      return `Scheduled<br>${this.formatDate(message?.schedule?.date)}`;
    } else if (message?.draft) {
      return 'Draft';
    }

    return 'Sent';
  }

  formatDate(isoString: string): string {
    let dateObject = new Date(isoString);

    // Get merchant timezone for proper display
    const merchantTimezone = this.getMerchantTimezone();

    // Format the date in the merchant's timezone
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: merchantTimezone,
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return formatter.format(dateObject);
  }

  getMerchantTimezone(): string {
    // Get merchant timezone, fallback to browser timezone
    const merchantTimezone = this.authService.getNestedUserProperty(
      'merchant',
      'timezone',
    );
    const merchantCountry = this.authService.getNestedUserProperty(
      'merchant',
      'country',
    );

    if (merchantTimezone) {
      return merchantTimezone;
    }

    // Fallback to country-based timezone mapping (simplified version)
    if (merchantCountry?.code) {
      const timezoneMap: { [key: string]: string } = {
        GB: 'Europe/London',
        US: 'America/New_York',
        CA: 'America/Toronto',
        AU: 'Australia/Sydney',
        NZ: 'Pacific/Auckland',
        IE: 'Europe/Dublin',
        FR: 'Europe/Paris',
        DE: 'Europe/Berlin',
        IT: 'Europe/Rome',
        ES: 'Europe/Madrid',
        NL: 'Europe/Amsterdam',
        BE: 'Europe/Brussels',
        CH: 'Europe/Zurich',
        AT: 'Europe/Vienna',
        SE: 'Europe/Stockholm',
        NO: 'Europe/Oslo',
        DK: 'Europe/Copenhagen',
        FI: 'Europe/Helsinki',
        JP: 'Asia/Tokyo',
        CN: 'Asia/Shanghai',
        IN: 'Asia/Kolkata',
        BR: 'America/Sao_Paulo',
        MX: 'America/Mexico_City',
        AR: 'America/Argentina/Buenos_Aires',
        ZA: 'Africa/Johannesburg',
        SG: 'Asia/Singapore',
        HK: 'Asia/Hong_Kong',
      };

      return timezoneMap[merchantCountry.code] || 'Europe/London';
    }

    // Final fallback to browser timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  seen(conversation: Conversation) {
    if (
      !(conversation.lastMessage as ChatMessage).seen &&
      (conversation.lastMessage as ChatMessage).authorType ===
        this.constants.userTypes.user
    ) {
      return false;
    }

    return true;
  }

  showInfoIcon(conversation: Conversation) {
    if (this.conversationStatus(conversation) === 'Blocked') {
      return true;
    }
    if (this.conversationStatus(conversation) === 'Muted') {
      return true;
    }
    return false;
  }

  infoIconText(conversation: Conversation): string {
    if (this.conversationStatus(conversation) === 'Blocked') {
      return 'You have blocked this customer from being able to message you';
    }
    if (this.conversationStatus(conversation) === 'Muted') {
      return 'You have muted this customer, you will not be notified when they message you';
    }
    return '';
  }

  get activeAutomationCount() {
    return this.numberOfActiveAutomations;
  }

  createAutomation(): void {
    if (!this.hasAutomationsAccess) {
      this.openUpgradeDialog('automations');
      return;
    }

    const dialogRef = this.dialog.open(AutomationDialogComponent, {
      data: {},
      scrollStrategy: new NoopScrollStrategy(),
      width: '800px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        window.location.reload();
      }
    });
  }
}
